import React, { useMemo, useState } from 'react';

const normalized = value => String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
const unitKey = value => normalized(value).replace(/^(HOURS?|HRS)$/, 'HR');
const taskNameKey = value => normalized(value)
  .replace(/\bACFT\b/g, 'AIRCRAFT').replace(/\bHOURS?\b/g, 'HR')
  .replace(/(\d)\s*-?\s*(HRS?)\b/g, '$1 HR');
const dateKey = value => /^\d{4}-\d{2}-\d{2}/.test(String(value || '')) ? String(value).slice(0, 10) : '';
const displayDate = value => {
  const date = dateKey(value);
  if (!date) return 'Not set';
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};
const waiverKey = waiver => normalized(waiver.key || waiver.reference) || waiver.id;
function addWaivers(target, waivers) {
  (waivers || []).filter(row => ['active', 'pending'].includes(row.status)).forEach(row => {
    const key = waiverKey(row);
    if (key && (!target.has(key) || row.status === 'active')) target.set(key, row);
  });
}

export function inspectionBacklog(tails, population, period, generatedAt) {
  const today = dateKey(generatedAt) || new Date().toISOString().slice(0, 10);
  const scoped = tails.filter(row => population === 'deployed'
    ? row.deployment?.active
    : normalized(row.aircraft?.hsOs) === 'OS' || row.deployment?.active);
  const groups = new Map();
  let missingReturns = 0;
  scoped.forEach(row => {
    const aircraft = row.aircraft || {};
    const returnAt = aircraft.estimatedReturnAt || row.deployment?.plannedReturnDate;
    const returnDay = dateKey(returnAt);
    if (!returnDay || returnDay < today) missingReturns += 1;
    const seenTasks = new Set();
    (row.backlog9188 || []).forEach(task => {
      if (!task.id || seenTasks.has(task.id)) return;
      seenTasks.add(task.id);
      const due = dateKey(task.dueAt);
      const included = period === 'overdue' ? task.level === 'overdue'
        : task.level !== 'overdue' && due && due >= today && returnDay >= today && due <= returnDay;
      if (!included) return;
      // Conservative identity: normalize spelling, but retain model, WUC and interval.
      const key = JSON.stringify([normalized(aircraft.mds).replace(/[^A-Z0-9]/g, ''), taskNameKey(task.noun),
        normalized(task.wuc), normalized(task.logicCode), task.increment ?? '', unitKey(task.dueUnit)]);
      if (!groups.has(key)) groups.set(key, { key, name: task.noun || 'Unnamed inspection', mds: aircraft.mds || 'Unspecified',
        wuc: task.wuc, tails: new Map(), waivers: new Map() });
      const group = groups.get(key);
      const id = aircraft.id || `${aircraft.location}|${aircraft.tail}`;
      if (!group.tails.has(id)) group.tails.set(id, { aircraft, returnAt, tasks: [], waivers: new Map() });
      const tail = group.tails.get(id);
      tail.tasks.push(task);
      addWaivers(tail.waivers, task.waivers);
      addWaivers(group.waivers, task.waivers);
    });
  });
  const allTails = new Set();
  const allWaivers = new Map();
  const rows = [...groups.values()].map(group => {
    group.tails.forEach((tail, id) => allTails.add(id));
    addWaivers(allWaivers, [...group.waivers.values()]);
    const waivers = [...group.waivers.values()];
    return { ...group, tails: [...group.tails.values()].sort((a, b) => String(a.aircraft.tail).localeCompare(String(b.aircraft.tail))),
      waivers, active: waivers.filter(row => row.status === 'active').length, pending: waivers.filter(row => row.status === 'pending').length };
  }).sort((a, b) => b.tails.length - a.tails.length || b.waivers.length - a.waivers.length || a.name.localeCompare(b.name));
  return { rows, jets: allTails.size, waivers: allWaivers.size, scopedJets: scoped.length, missingReturns };
}

export default function InspectionBacklog({ tails = [], generatedAt, onOpenAircraft }) {
  const [population, setPopulation] = useState('deployed');
  const [period, setPeriod] = useState('overdue');
  const backlog = useMemo(() => inspectionBacklog(tails, population, period, generatedAt), [tails, population, period, generatedAt]);
  const largest = Math.max(1, ...backlog.rows.map(row => row.tails.length));
  return <section className="reports-panel crystal-ball__inspection-backlog" data-pdf-section="inspection-backlog">
    <div className="reports-panel__header">
      <div><h3>Work Waiting on {population === 'deployed' ? 'Deployed' : 'Offstation'} Jets</h3><span>Which inspections are piling up, and how many jets and waivers are involved?</span></div>
    </div>
    <div className="crystal-ball__backlog-controls">
      <label>Jets <select aria-label="Backlog aircraft scope" value={population} onChange={event => setPopulation(event.target.value)}>
        <option value="deployed">Deployed jets</option><option value="offstation">Deployed + other offstation jets</option>
      </select></label>
      <div role="group" aria-label="Backlog period">
        <button type="button" aria-pressed={period === 'overdue'} onClick={() => setPeriod('overdue')}>Overdue now</button>
        <button type="button" aria-pressed={period === 'before-return'} onClick={() => setPeriod('before-return')}>Due before coming home</button>
      </div>
    </div>
    <div className="crystal-ball__backlog-totals">
      <span><strong>{backlog.jets}</strong> jets with work waiting</span>
      <span><strong>{backlog.rows.length}</strong> inspection types</span>
      <span><strong>{backlog.waivers}</strong> distinct waivers linked</span>
    </div>
    <p className="crystal-ball__status-note">{period === 'overdue' ? 'Overdue open 9188-6 tasks, including waived work.' : 'Upcoming tasks due by the expected return date; overdue work stays in Overdue now.'} Waivers include active and pending status.</p>
    {period === 'before-return' && backlog.missingReturns > 0 ? <p className="crystal-ball__status-note">{backlog.missingReturns} jets need a current return date before upcoming work can be counted.</p> : null}
    <div className="crystal-ball__backlog-heading" aria-hidden="true"><span>Inspection task</span><span>Jets needing it</span><span>Waivers linked</span></div>
    {backlog.rows.map(group => <details className="crystal-ball__backlog-group" key={`${population}|${period}|${group.key}`}>
      <summary>
        <span><strong>{group.name}</strong><small>{group.mds}{group.wuc ? ` · WUC ${group.wuc}` : ''}</small></span>
        <span className="crystal-ball__backlog-jets"><strong>{group.tails.length} {group.tails.length === 1 ? 'jet' : 'jets'}</strong><i aria-hidden="true"><b style={{ width: `${group.tails.length / largest * 100}%` }} /></i></span>
        <span><strong>{group.waivers.length} {group.waivers.length === 1 ? 'waiver' : 'waivers'}</strong><small>{group.active} active · {group.pending} pending</small></span>
      </summary>
      <div className="reports-table-shell"><table className="reports-table reports-status-table">
        <thead><tr><th>Tail</th><th>Coming home</th><th>Due / work plan</th><th>Waivers</th></tr></thead>
        <tbody>{group.tails.map(tail => <tr key={tail.aircraft.id || `${tail.aircraft.location}|${tail.aircraft.tail}`}>
          <td><button type="button" onClick={() => onOpenAircraft(tail.aircraft)}>{tail.aircraft.tail}</button><small>{tail.aircraft.location}</small></td>
          <td>{displayDate(tail.returnAt)}</td>
          <td>{tail.tasks.map(task => <div key={task.id}>
            <span>Due: {task.dueAt ? displayDate(task.dueAt) : task.level === 'overdue' ? 'Overdue by source / usage limit' : 'Not set'}</span>
            <small>{task.plannedStartAt && task.plannedFinishAt ? `${displayDate(task.plannedStartAt)} → ${displayDate(task.plannedFinishAt)}` : 'Work plan needs dates'}</small>
          </div>)}</td>
          <td><strong>{tail.waivers.size} linked</strong>{[...tail.waivers.values()].map(waiver => <small key={waiverKey(waiver)}>{waiver.reference || 'Waiver'} · {waiver.status === 'active' ? 'Active' : 'Pending Issued Date'}</small>)}</td>
        </tr>)}</tbody>
      </table></div>
    </details>)}
    {!backlog.rows.length ? <p className="crystal-ball__backlog-empty">{backlog.scopedJets ? `No ${period === 'overdue' ? 'overdue' : 'known upcoming'} inspection tasks match this view.` : `No ${population === 'deployed' ? 'jets with an active Deployment plan' : 'offstation or deployed jets'} match the current filters.`}</p> : null}
    <p className="crystal-ball__status-note">Select an inspection to see its tails. Each jet and waiver counts once per inspection; the same waiver can cover more than one inspection type. Upcoming totals use known due and return dates.</p>
  </section>;
}
